import { Button, Col, Collapse, Form, Input, Row, Space } from "antd";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import CaretDownIcon from "../../../assets/icons/CaretDownIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import FunnelIcon from "../../../assets/icons/FunnelIcon";
import ReportGenerateIcon from "../../../assets/icons/ReportGenerateIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import TicketsIcon from "../../../assets/icons/TicketsIcon";
import ActionModal from "../../../components/ui/ActionModal";
import CustomModal from "../../../components/ui/CustomModal";
import { useGetMeQuery } from "../../../features/auth/authApi";
import { useGenerateReportMutation } from "../../../features/superAdmin/ticketService/report/report.api";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { TEMPLATE_TYPE } from "../TicketManagement/constants/ticket.constants";
import ManageTicketForm from "../TicketManagement/Tickets/_components/ManageTicketForm";
import DateRangePicker from "./_components/DateRangePicker";
import FilterDrawer from "./_components/FilterDrawer";
import MyDeptTicketStates from "./_components/MyDeptTicketStates";
import OrganizationStates from "./_components/OrganizationStates";
import States from "./_components/States";
import TicketPerformance from "./_components/TicketPerformance";
import TopPerformers from "./_components/TopPerformers";

export type DFilters = {
  skip?: number;
  limit?: number;
  search?: string;
  fromDate?: string;
  toDate?: string;
  department_id?: number;
  product_id?: number;
  feature_id?: number;
  template_type?: TEMPLATE_TYPE;
  severity?: string;
  priority?: string;
  sla?: string;
};
type TModal = "ACTION" | "FILTER" | "SUCCESS";

export default function Dashboard() {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { filter, keyword, handleFilter, handleSearch } = useTable<DFilters>();
  const { open, handleOpen, handleClose } = useModal<unknown, TModal>();
  const { user, role } = useAppSelector((state) => state.auth);
  const { data } = useGetMeQuery(user?.id);
  const { can_download_reports } = data?.data || {};
  const canDownloadReport =
    can_download_reports || role === "Admin" || role === "super_admin";

  const { skip, limit, toDate, fromDate, search, ...filters } = filter || {};
  const filledCount = Object.values(filters).filter(
    (value) => value !== undefined && value !== null && value !== "",
  ).length;

  // ------ Mutations ------
  const [
    generateReport,
    {
      isLoading: generating,
      isError: isGenerateError,
      error: generateError,
      isSuccess,
    },
  ] = useGenerateReportMutation();

  // ------ Function for clear filter ------
  const handleFilterClear = () => {
    handleFilter("department_id", undefined);
    handleFilter("product_id", undefined);
    handleFilter("feature_id", undefined);
    handleFilter("template_type", undefined);
    handleFilter("severity", undefined);
    handleFilter("priority", undefined);
    form.resetFields();
  };

  const {
    department_id,
    feature_id,
    product_id,
    severity,
    priority,
    template_type,
  } = filter || {};

  const isFiltered =
    department_id ||
    feature_id ||
    product_id ||
    severity ||
    priority ||
    template_type;

  useEffect(() => {
    if (isSuccess && !generating) {
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, generating]);

  useError(isGenerateError, generateError, "Report generate failed.");
  return (
    <SidebarLayout title="Dashboard">
      <div className="space-y-6">
        <Space className="flex items-center justify-between">
          <Input
            size="middle"
            className="h-9"
            prefix={<SearchIcon />}
            placeholder="Search tickets"
            onChange={handleSearch}
          />
          <Space>
            <Button
              onClick={() => handleOpen({ type: "FILTER" })}
              className={`h-9 ${
                isFiltered ? "button-primary-light" : "button-bordered-light"
              }`}
            >
              <FunnelIcon />
              Filters
              {!!filledCount && ` (${filledCount})`}
            </Button>

            {!!filledCount && (
              <Button
                onClickCapture={handleFilterClear}
                onClick={() => handleOpen({ type: "FILTER" })}
                className={`h-9 button-bordered-light !min-w-10`}
              >
                <CloseIcon />
              </Button>
            )}

            <DateRangePicker handleFilter={handleFilter} />
            {!!canDownloadReport && (
              <Button
                onClick={() => handleOpen({ type: "ACTION" })}
                className="button-primary-light h-9"
              >
                <ReportGenerateIcon />
                Generate Report
              </Button>
            )}
            <Button
              onClick={() => handleOpen({ type: "ADD" })}
              className="button-primary-light h-9"
            >
              <TicketsIcon />
              Create Ticket
            </Button>
          </Space>
        </Space>
        {/* States */}
        {role === "super_admin" || role === "Admin" ? (
          <Collapse
            onChange={() => {
              window.scrollTo({
                top: 0,
                behavior: "smooth", // optional, for smooth scrolling
              });
            }}
            accordion
            defaultActiveKey={["1"]}
            bordered={false}
          >
            <CollapsePanel
              className="!bg-white"
              header={
                <Space className="flex items-center justify-between w-full bg-slate-50 px-4 py-2 rounded-lg">
                  <h5 className="text-small font-medium text-display-body">
                    Organization
                  </h5>
                  <CaretDownIcon />
                </Space>
              }
              key="1"
            >
              <OrganizationStates search={keyword} {...filter} />{" "}
              {/*Filter Done*/}
            </CollapsePanel>
            <CollapsePanel
              className="!bg-white"
              header={
                <Space className="flex items-center justify-between w-full bg-slate-50 px-4 py-2 rounded-lg">
                  <h5 className="text-small font-medium text-display-body">
                    Tickets of my department
                  </h5>
                  <CaretDownIcon />
                </Space>
              }
              key="2"
            >
              <States search={keyword} {...filter} /> {/*Filter Done*/}
            </CollapsePanel>
          </Collapse>
        ) : (
          <States search={keyword} {...filter} />
        )}
        <Row gutter={24}>
          <Col span={15}>
            {/* Ticket Performance */}
            <TicketPerformance search={keyword} {...filter} />
          </Col>
          <Col span={9}>
            <TopPerformers search={keyword} {...filter} /> {/*Filter Done*/}
          </Col>
        </Row>
        {/* Ticket of my department */}
        <MyDeptTicketStates search={keyword} {...filter} /> {/*Filter Done*/}
        <CustomModal
          title={undefined}
          open={open?.open && open.type === "ADD"}
          handleClose={handleClose}
          width={738}
          closable={false}
          footer={null}
        >
          <ManageTicketForm key={1} onClose={handleClose} />
        </CustomModal>
        {/* Top Performers */}
        <div>
          <FilterDrawer
            form={form}
            handleClose={handleClose}
            open={open.open && open.type === "FILTER"}
            isFiltered={!!isFiltered}
            handleFilter={handleFilter}
          />
          <ActionModal
            open={open?.open && open.type === "ACTION"}
            loading={generating}
            title={"Generate Report"}
            subTitle={
              "Are you sure you want to generate this report with the selected filters? This may take a few minutes."
            }
            iconType="warning"
            actionBtnText={`Yes, Generate`}
            actionBtnType="primary-light"
            handleClose={handleClose}
            handleAction={() => {
              const { skip, limit, ...filters } = filter || {};
              generateReport({
                data: { name: "Dashboard", ...filters, search: keyword },
              });
            }}
            handleCancel={handleClose}
          />
          <ActionModal
            open={open?.open && open.type === "SUCCESS"}
            loading={generating}
            title={"Report Generated Successfully"}
            subTitle={
              "Your report has been generated and is ready for download."
            }
            iconType="success"
            actionBtnText={`Download Report`}
            cancelText="Ok"
            actionBtnType="primary-light"
            handleClose={handleClose}
            handleAction={() => {
              handleClose();
              navigate(`/${role === "User" ? "admin" : "user"}/reports`);
            }}
            handleCancel={handleClose}
            cancelButton={false}
            closeable
          />
        </div>
      </div>
    </SidebarLayout>
  );
}
